import { PageLoader } from '@/components/ui/loader';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, CreditCard, FileDown, Loader2, Lock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError, apiClient, apiDelete, apiGet, apiPost } from '@/lib/api';

// ── PDFs ─────────────────────────────────────────────────────────────────────

/** Opens an API PDF in a new tab, carrying the session the way every request does. */
export async function openPdf(path: string, fileName: string) {
  const preview = window.open('', '_blank');
  try {
    const response = await apiClient.get(path, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data as BlobPart], { type: 'application/pdf' }));
    if (preview) preview.location.href = url;
    else {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    preview?.close();
    toast.error('The PDF could not be made');
  }
}

export function PdfButton({ path, fileName, label = 'PDF' }: { path: string; fileName: string; label?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        await openPdf(path, fileName);
        setBusy(false);
      }}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
      {label}
    </Button>
  );
}

// ── Gateway settings ─────────────────────────────────────────────────────────

type Gateway = {
  provider: 'telr' | 'ngenius' | 'stripe';
  label: string;
  testMode: boolean;
  isActive: boolean;
  storeId: string | null;
  outletRef: string | null;
  authKey: string | null;
  apiKey: string | null;
  secretKey: string | null;
} | null;

const PROVIDERS = [
  { value: 'telr', label: 'Telr', hint: 'Telr Merchant Admin → Integrations → Gateway: the store ID and the authentication key (Hosted Payment Page).' },
  { value: 'ngenius', label: 'Network International (N-Genius)', hint: 'N-Genius portal → Settings → Integrations → Service accounts: the API key, and the outlet reference.' },
  { value: 'stripe', label: 'Stripe', hint: 'Stripe Dashboard → Developers → API keys: the secret key (sk_test_… or sk_live_…).' },
] as const;

/**
 * The card gateway settings. `scope` is the workspace (its invoices) or the
 * platform (DovoCRM subscriptions); the form is the same.
 */
export function GatewaySettings({ scope }: { scope: 'workspace' | 'platform' }) {
  const path = scope === 'workspace' ? '/commerce/gateway' : '/platform/gateway';
  const queryClient = useQueryClient();
  const { data: gateway, isLoading } = useQuery({ queryKey: ['gateway', scope], queryFn: () => apiGet<Gateway>(path) });
  const [provider, setProvider] = useState<'telr' | 'ngenius' | 'stripe'>('telr');
  const [fields, setFields] = useState({ storeId: '', authKey: '', apiKey: '', outletRef: '', secretKey: '' });
  const [testMode, setTestMode] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gateway) return;
    setProvider(gateway.provider);
    setTestMode(gateway.testMode);
    setIsActive(gateway.isActive);
    setFields({ storeId: gateway.storeId ?? '', authKey: '', apiKey: '', outletRef: gateway.outletRef ?? '', secretKey: '' });
  }, [gateway]);

  const save = useMutation({
    mutationFn: () => apiClient.put(path, { provider, ...fields, testMode, isActive }),
    onSuccess: () => {
      toast.success('Saved');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['gateway', scope] });
    },
    onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not save'),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(path),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['gateway', scope] }),
  });
  const set = (key: keyof typeof fields, value: string) => setFields((current) => ({ ...current, [key]: value }));
  const saved = gateway && gateway.provider === provider ? gateway : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-4 w-4" /> {scope === 'workspace' ? 'Online card payments' : 'Subscription payments'}
          {gateway && (
            <Badge className={gateway.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>
              {gateway.isActive ? (gateway.testMode ? 'Test mode' : 'Live') : 'Off'}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {scope === 'workspace'
            ? 'Clients pay invoices by card from the invoice link. The payment is recorded and the invoice marked paid on its own.'
            : 'Workspaces pay for their DovoCRM plan through this gateway.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            <div className="space-y-1.5">
              <Label>Gateway</Label>
              <Select value={provider} onChange={(event) => setProvider(event.target.value as typeof provider)}>
                {PROVIDERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">{PROVIDERS.find((item) => item.value === provider)?.hint}</p>
            </div>
            {provider === 'telr' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Store ID</Label>
                  <Input value={fields.storeId} onChange={(event) => set('storeId', event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Authentication key</Label>
                  <Input type="password" value={fields.authKey} placeholder={saved?.authKey ?? ''} onChange={(event) => set('authKey', event.target.value)} />
                </div>
              </div>
            )}
            {provider === 'ngenius' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>API key</Label>
                  <Input type="password" value={fields.apiKey} placeholder={saved?.apiKey ?? ''} onChange={(event) => set('apiKey', event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Outlet reference</Label>
                  <Input value={fields.outletRef} onChange={(event) => set('outletRef', event.target.value)} />
                </div>
              </div>
            )}
            {provider === 'stripe' && (
              <div className="space-y-1.5">
                <Label>Secret key</Label>
                <Input type="password" value={fields.secretKey} placeholder={saved?.secretKey ?? 'sk_test_…'} onChange={(event) => set('secretKey', event.target.value)} />
              </div>
            )}
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" /> Keys are encrypted and never shown again. Leave a key blank to keep the saved one.
            </p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={testMode} onCheckedChange={setTestMode} /> Test mode
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={isActive} onCheckedChange={setIsActive} /> Taking payments
              </label>
            </div>
            <FormError message={error} />
            <div className="flex gap-2">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              {gateway && scope === 'workspace' && (
                <Button variant="ghost" onClick={() => window.confirm('Remove the gateway? Invoice links stop offering card payment.') && remove.mutate()}>
                  Remove
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Paying an invoice ────────────────────────────────────────────────────────

/** "Pay now" and "Download PDF" on a public invoice. */
export function InvoicePayBar({ token, balance }: { token: string; balance: string }) {
  const { data } = useQuery({
    queryKey: ['payable', token],
    queryFn: () => apiGet<{ payable: boolean; provider: string | null }>(`/public/invoices/${token}/payable`),
    retry: false,
  });
  const pay = useMutation({
    mutationFn: () => apiPost<{ url: string }>(`/public/invoices/${token}/pay`),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (failure) => toast.error(failure instanceof ApiRequestError ? failure.message : 'Could not open the payment page'),
  });
  const base = import.meta.env.VITE_API_URL ?? '/api/v1';

  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-2 print:hidden">
      <Button variant="outline" asChild>
        <a href={`${base}/public/invoices/${token}/pdf`}>
          <FileDown className="h-4 w-4" /> Download PDF
        </a>
      </Button>
      {data?.payable && (
        <Button onClick={() => pay.mutate()} disabled={pay.isPending}>
          {pay.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          Pay {balance} by card
        </Button>
      )}
    </div>
  );
}

type Result = { status: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED'; amount: string; currency: string; description: string; backUrl: string };

/** /pay/return/<attempt> — where the gateway sends the customer back. */
export function PaymentReturnPage() {
  const { attemptId = '' } = useParams();
  const [params] = useSearchParams();
  const [tries, setTries] = useState(0);
  const { data, isError } = useQuery({
    queryKey: ['pay-return', attemptId, tries],
    queryFn: () => apiPost<Result>(`/public/payments/${attemptId}/verify`),
    retry: false,
  });

  // A gateway can take a few seconds to confirm; ask again, a handful of times.
  useEffect(() => {
    if (data?.status === 'PENDING' && tries < 6 && !params.get('cancelled')) {
      const timer = setTimeout(() => setTries((value) => value + 1), 3_000);
      return () => clearTimeout(timer);
    }
  }, [data, tries, params]);

  const status = isError ? 'FAILED' : data?.status;
  const icon =
    status === 'PAID' ? <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" /> : status === 'PENDING' || !status ? <Loader2 className="mx-auto h-14 w-14 animate-spin text-primary" /> : <XCircle className="mx-auto h-14 w-14 text-red-500" />;
  const title =
    status === 'PAID'
      ? 'Payment received'
      : status === 'PENDING' || !status
        ? params.get('cancelled')
          ? 'Payment not completed'
          : 'Confirming your payment…'
        : status === 'CANCELLED'
          ? 'Payment cancelled'
          : 'Payment did not go through';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-md rounded-3xl border bg-white p-8 text-center shadow-xl">
        {icon}
        <h1 className="mt-4 text-2xl font-extrabold text-slate-900">{title}</h1>
        {data && (
          <p className="mt-2 text-slate-600">
            {data.description} · {data.currency} {data.amount}
          </p>
        )}
        {status === 'PAID' && <p className="mt-2 text-sm text-slate-500">A receipt is on its way. You can close this page.</p>}
        {(status === 'FAILED' || status === 'CANCELLED') && <p className="mt-2 text-sm text-slate-500">No money was taken. You can try again.</p>}
        {data?.backUrl && (
          <Button className="mt-6" asChild>
            {data.backUrl.startsWith(window.location.origin) ? <Link to={data.backUrl.replace(window.location.origin, '')}>Back</Link> : <a href={data.backUrl}>Back</a>}
          </Button>
        )}
      </div>
    </div>
  );
}
